/**
 * PreproductionWizard - Multi-step project task generation wizard
 * Stitch design system
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Dialog, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Radio, RadioGroup, FormControl,
  Accordion, AccordionSummary, AccordionDetails, TextField, Autocomplete, Tooltip,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

import ProjectTypeSelector from './ProjectTypeSelector';
import ConditionQuestionnaire from './ConditionQuestionnaire';
import PhaseKanbanView from './PhaseKanbanView';
import CalendarGridView from './CalendarGridView';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
    {children}
  </Typography>
);

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.82rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody, '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' }, '&.Mui-focused fieldset': { borderColor: T.primary } },
  '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.82rem' },
};

const roleColors = {
  'Projectleider': '#10b981', 'Accountmanager': '#3b82f6', 'Regisseur': '#7c3aed',
  'Camera Operator': '#06b6d4', 'Producer': '#f59e0b', 'Editor': '#3b82f6',
  'Animator': '#ec4899', 'Copywriter': '#8b5cf6', 'Productie-assistent': '#f97316', 'Management': '#6b7280',
};

const STEPS = ['Project Type', 'Condities', 'Taken overzicht', 'Planning', 'Rolverdeling', 'Review & Genereer'];

const GRIPP_FORMAT_TO_WIZARD_TYPE = {
  'Commercial': 'commercial', 'Radiocommercial': 'commercial', 'Bedrijfsfilm': 'corporate',
  'Projectfilm': 'corporate', 'Productfilm': 'corporate', 'Testimonial': 'interview',
  'Interview': 'interview', 'Eventfilm': 'event', 'Registratie': 'event', 'Animatie': 'animation',
  'Explainer': 'corporate', 'Podcast': 'interview', 'Webinar': 'event', 'Fotografie': 'social', 'Shortform video': 'social', 'Infographic': 'animation',
};
const GRIPP_FLAG_TO_CONDITIONS = {
  'Acteurs / presentator': { cast_needed: true }, 'Meerdere locaties': { multiple_locations: true },
  'Voice-over': { voice_over_needed: true }, 'Motion graphics': { animation_needed: true },
  'Drone': { outdoor_shoot: true }, 'Interview': { script_needed: true },
};
const GRIPP_OMVANG_TO_CONDITIONS = { 'L': { external_crew: true }, 'XL': { external_crew: true, multiple_locations: true } };
const GRIPP_DIENST_TO_CONDITIONS = { 'Volledige productie': { location_needed: true, script_needed: true }, 'Productie (opname)': { location_needed: true } };

const PreproductionWizard = ({ open, onClose, project, onOpenTemplateManager }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [personen, setPersonen] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState('');
  const [workflowType, setWorkflowType] = useState('preproductie');
  const [projectCategory, setProjectCategory] = useState('B');
  const [availableConditions, setAvailableConditions] = useState([]);
  const [conditions, setConditions] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTree, setTemplateTree] = useState(null);
  const [taskSelections, setTaskSelections] = useState({});
  const [viewMode, setViewMode] = useState('phase');
  const [weekOffset, setWeekOffset] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [roleAssignments, setRoleAssignments] = useState({});
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => {
    if (open) {
      loadTemplates(); loadPersonen(); loadProjectTypesAndConditions();
      setActiveStep(0); setGeneratedResult(null); setPushResult(null); setError(null); setSelectedProjectType(''); setConditions({});
      const today = new Date(); const fw = new Date(today); fw.setDate(fw.getDate() + 28);
      setStartDate(today.toISOString().split('T')[0]); setEndDate(fw.toISOString().split('T')[0]);
    }
  }, [open]);

  const loadPersonen = async () => { try { const r = await authFetch(`${BACKEND_URL}/Api/personen`); const d = await leesJson(r, 'Personen laden'); setPersonen(d.people || []); } catch (e) { console.error('Failed to load personen:', e); setError(e.message || 'Kon personen niet laden.'); } };

  const prefillFromGripp = (loadedTypes) => {
    if (!project?.fields) return;
    const f = project.fields;
    const mappedType = GRIPP_FORMAT_TO_WIZARD_TYPE[f.Projecttype];
    if (mappedType) setSelectedProjectType(mappedType);
    const autoConditions = {};
    if (Array.isArray(f.Flags)) f.Flags.forEach(flag => { const m = GRIPP_FLAG_TO_CONDITIONS[flag]; if (m) Object.assign(autoConditions, m); });
    const oc = GRIPP_OMVANG_TO_CONDITIONS[f.Omvang]; if (oc) Object.assign(autoConditions, oc);
    const dc = GRIPP_DIENST_TO_CONDITIONS[f.Dienst]; if (dc) Object.assign(autoConditions, dc);
    if (mappedType) { const to = loadedTypes.find(t => t.code === mappedType); setConditions({ ...(to?.default_conditions || {}), ...autoConditions }); }
    else if (Object.keys(autoConditions).length > 0) setConditions(autoConditions);
  };

  const loadProjectTypesAndConditions = async () => {
    try {
      await authFetch(`${BACKEND_URL}/Api/workflow-templates/seed-types`, { method: 'POST' });
      const [typesRes, condsRes] = await Promise.all([authFetch(`${BACKEND_URL}/Api/workflow-templates/project-types`), authFetch(`${BACKEND_URL}/Api/workflow-templates/conditions`)]);
      const [typesData, condsData] = await Promise.all([leesJson(typesRes, 'Projecttypes laden'), leesJson(condsRes, 'Condities laden')]);
      const lt = typesData.project_types || []; setProjectTypes(lt); setAvailableConditions(condsData.conditions || []);
      prefillFromGripp(lt);
    } catch (e) { console.error('Failed to load project types/conditions:', e); setError(e.message || 'Kon projecttypes/condities niet laden.'); }
  };

  useEffect(() => {
    if (templates.length > 0) {
      const match = templates.find(t => t.workflow_type === workflowType && t.project_categories.split(',').includes(projectCategory));
      if (match && match.id !== selectedTemplate?.id) { setSelectedTemplate(match); loadTemplateTree(match.id); }
    }
  }, [workflowType, projectCategory, templates]);

  const loadTemplates = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates`); const d = await leesJson(r, 'Templates laden');
      if (d.templates) setTemplates(d.templates);
      else { await authFetch(`${BACKEND_URL}/Api/workflow-templates/seed`, { method: 'POST' }); const r2 = await authFetch(`${BACKEND_URL}/Api/workflow-templates`); const d2 = await leesJson(r2, 'Templates laden'); setTemplates(d2.templates || []); }
    } catch (e) { console.error('Failed to load templates:', e); setError(e.message || 'Kon templates niet laden.'); }
  };

  const loadTemplateTree = async (templateId) => {
    try {
      setLoading(true); const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/${templateId}`); const d = await leesJson(r, 'Template laden');
      if (d.template) {
        setTemplateTree(d.template);
        const sel = {}; d.template.phases?.forEach(phase => { phase.tasks?.forEach(task => { const tc = task.conditions || {}; let inc = true; if (tc.only_categories && !tc.only_categories.includes(projectCategory)) inc = false; if (tc.only_if && !conditions[tc.only_if]) inc = false; sel[task.id] = { included: inc, name: task.name, hours: task.estimated_hours || 0, role: task.role || '', conditionsMet: inc }; }); });
        setTaskSelections(sel);
        const roles = new Set(); d.template.phases?.forEach(phase => { phase.tasks?.forEach(task => { if (task.role) roles.add(task.role); }); }); setUniqueRoles([...roles]);
      }
    } catch (e) { console.error('Failed to load template tree:', e); setError(e.message || 'Kon template niet laden.'); } finally { setLoading(false); }
  };

  const handleTaskToggle = (taskId) => setTaskSelections(prev => ({ ...prev, [taskId]: { ...prev[taskId], included: !prev[taskId]?.included } }));
  const handleTaskFieldChange = (taskId, field, value) => setTaskSelections(prev => ({ ...prev, [taskId]: { ...prev[taskId], [field]: value } }));
  const handleRoleAssign = (role, person) => setRoleAssignments(prev => ({ ...prev, [role]: person ? { id: person.id, name: person.name } : null }));

  const handleGenerate = async () => {
    try {
      setLoading(true); setError(null);
      const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ template_id: selectedTemplate.id, airtable_project_id: project?.id || project?.airtableId, project_name: project?.fields?.Projectnaam || project?.name || '', project_category: projectCategory, project_type: selectedProjectType, conditions, role_assignments: roleAssignments, start_date: startDate, end_date: endDate, generated_by: '' }) });
      const d = await leesJson(r, 'Taken genereren'); if (d.success) setGeneratedResult(d); else setError(d.error || 'Generatie mislukt');
    } catch (e) { setError('Fout bij genereren: ' + e.message); } finally { setLoading(false); }
  };

  const handlePushToAirtable = async () => {
    if (!generatedResult?.task_set?.id) return;
    try {
      setPushing(true); setError(null);
      const r = await authFetch(`${BACKEND_URL}/Api/workflow/push-to-airtable`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ task_set_id: generatedResult.task_set.id, project_id: project?.id || project?.airtableId }) });
      const d = await leesJson(r, 'Taken naar Airtable pushen'); if (d.success) setPushResult(d); else setError(d.error || 'Push mislukt');
    } catch (e) { setError('Fout bij pushen: ' + e.message); } finally { setPushing(false); }
  };

  const selectedTaskCount = Object.values(taskSelections).filter(t => t.included).length;
  const totalTasks = Object.keys(taskSelections).length;
  const totalHours = Object.values(taskSelections).filter(t => t.included).reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
  const canProceed = () => { switch (activeStep) { case 0: return selectedProjectType !== ''; case 1: return true; case 2: return selectedTemplate != null; case 3: return startDate && endDate; case 4: return true; case 5: return true; default: return false; } };

  /* ── Step renderers ──────────────────────────────────────────────────── */

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ProjectTypeSelector projectTypes={projectTypes} selectedType={selectedProjectType}
        onSelectType={(code) => { setSelectedProjectType(code); const type = projectTypes.find(t => t.code === code); if (type?.default_conditions) setConditions(type.default_conditions); }}
        onConditionsChange={setConditions} />

      {project?.fields?.Projecttype && GRIPP_FORMAT_TO_WIZARD_TYPE[project.fields.Projecttype] && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: `${T.primary}08`, border: `1px solid ${T.primary}15` }}>
          <Icon name="info" size={18} sx={{ color: T.primary, marginTop: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.6 }}>
            Automatisch ingevuld op basis van Gripp: <strong>{project.fields.Projecttype}</strong>
            {project.fields.Omvang && <> — Omvang: <strong>{project.fields.Omvang}</strong></>}
            {project.fields.Dienst && <> — Dienst: <strong>{project.fields.Dienst}</strong></>}
          </Typography>
        </Box>
      )}

      <Box sx={{ height: '1px', bgcolor: T.surfaceLow }} />

      <Box>
        <SectionLabel>Workflow fase</SectionLabel>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {['opstartfase', 'offertefase', 'preproductie', 'productie', 'afronding', 'evaluatie'].map(v => (
            <Box key={v} onClick={() => setWorkflowType(v)} sx={{
              px: 1.5, py: 0.5, borderRadius: '9999px', cursor: 'pointer',
              bgcolor: workflowType === v ? T.primaryContainer : T.surfaceLow,
              border: `1.5px solid ${workflowType === v ? `${T.primary}40` : 'transparent'}`,
              transition: 'all 0.15s',
              '&:hover': workflowType !== v ? { bgcolor: T.surfaceHigh } : {},
            }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: workflowType === v ? 700 : 500, color: workflowType === v ? T.primary : T.outline, textTransform: 'capitalize' }}>
                {v === 'opstartfase' ? 'Opstartfase' : v === 'offertefase' ? 'Offertefase' : v === 'preproductie' ? 'Pre-productie' : v.charAt(0).toUpperCase() + v.slice(1)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {selectedTemplate && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: `${T.primary}08`, border: `1px solid ${T.primary}15` }}>
          <Icon name="info" size={18} sx={{ color: T.primary, marginTop: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
            Template: <strong>{selectedTemplate.name}</strong> — {selectedTemplate.description}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderStep2Conditions = () => (
    <Box>
      <ConditionQuestionnaire conditions={availableConditions} selectedConditions={conditions} onConditionsChange={setConditions} />
      <Box sx={{ mt: 3, display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: `${T.primary}08`, border: `1px solid ${T.primary}15` }}>
        <Icon name="info" size={18} sx={{ color: T.primary, marginTop: 1 }} />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.6 }}>
          Geselecteerde condities bepalen welke taken worden gegenereerd. Je kunt later nog individuele taken aan/uitzetten.
        </Typography>
      </Box>
    </Box>
  );

  const renderStep3Kanban = () => {
    const allTasks = [];
    templateTree?.phases?.forEach(phase => {
      phase.tasks?.forEach(task => {
        const sel = taskSelections[task.id] || {};
        allTasks.push({ ...task, phase_name: phase.name, is_included: sel.included, name: sel.name || task.name, estimated_hours: sel.hours || task.estimated_hours || 0, start_date: sel.start_date || task.start_date, end_date: sel.end_date || task.end_date });
      });
    });
    const handleTaskDateChange = (taskId, s, e) => setTaskSelections(prev => ({ ...prev, [taskId]: { ...prev[taskId], start_date: s, end_date: e } }));

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>{selectedTaskCount}/{totalTasks} taken — {totalHours} uur</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[{ v: 'phase', icon: 'view_week', label: 'Fase' }, { v: 'calendar', icon: 'calendar_month', label: 'Kalender' }].map(opt => (
              <Tooltip key={opt.v} title={opt.label}>
                <Box onClick={() => setViewMode(opt.v)} sx={{ width: 32, height: 32, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: viewMode === opt.v ? T.primaryContainer : 'transparent', transition: 'all 0.15s', '&:hover': viewMode !== opt.v ? { bgcolor: T.surfaceLow } : {} }}>
                  <Icon name={opt.icon} size={18} sx={{ color: viewMode === opt.v ? T.primary : T.outlineVariant }} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {templateTree ? (
          viewMode === 'phase' ? (
            <PhaseKanbanView tasks={allTasks} conditions={conditions} onTaskToggle={(task) => handleTaskToggle(task.id)} showExcluded compact={false} />
          ) : (
            <CalendarGridView tasks={allTasks} startDate={startDate} endDate={endDate} onTaskClick={(task) => handleTaskToggle(task.id)} onTaskDateChange={handleTaskDateChange} showExcluded weeksToShow={6} weekOffset={weekOffset} onWeekOffsetChange={setWeekOffset} />
          )
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
            <Icon name="warning" size={18} sx={{ color: T.amber }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#92400e' }}>Geen template geladen. Ga terug en selecteer een project type.</Typography>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <SectionLabel>Taken aanpassen</SectionLabel>
          {templateTree?.phases?.map(phase => (
            <Accordion key={phase.id} sx={{ mb: 1, borderRadius: '0.75rem !important', boxShadow: T.shadow, '&:before': { display: 'none' }, '& .MuiAccordionSummary-root': { minHeight: 44 } }}>
              <AccordionSummary expandIcon={<Icon name="expand_more" size={18} sx={{ color: T.outline }} />}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 700, color: T.text }}>{phase.name}</Typography>
                <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: T.primary, bgcolor: T.primaryContainer, px: 1, py: 0.2, borderRadius: '9999px', ml: 1 }}>
                  {phase.tasks?.filter(t => taskSelections[t.id]?.included).length}/{phase.tasks?.length}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {phase.tasks?.map(task => {
                  const sel = taskSelections[task.id] || {};
                  return (
                    <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, borderRadius: '0.5rem', mb: 0.5, opacity: sel.included ? 1 : 0.5, bgcolor: sel.included ? `${T.tertiary}06` : 'transparent', '&:hover': { bgcolor: `${T.tertiary}10` } }}>
                      <Checkbox checked={sel.included || false} onChange={() => handleTaskToggle(task.id)} size="small" sx={{ p: 0.25 }} />
                      <TextField value={sel.name || ''} onChange={(e) => handleTaskFieldChange(task.id, 'name', e.target.value)} size="small" variant="standard" sx={{ flex: 1, '& .MuiInput-underline:before': { borderBottom: 'none' }, '& .MuiInput-input': { fontFamily: T.fontBody, fontSize: '0.78rem' } }} />
                      {sel.role && (
                        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: '#fff', bgcolor: roleColors[sel.role] || T.outline, px: 0.75, py: 0.15, borderRadius: '9999px' }}>{sel.role}</Typography>
                      )}
                      <TextField value={sel.hours || 0} onChange={(e) => handleTaskFieldChange(task.id, 'hours', parseFloat(e.target.value) || 0)} size="small" type="number" sx={{ width: 65, ...inputSx }} InputProps={{ endAdornment: <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>u</Typography> }} />
                      {!sel.conditionsMet && <Tooltip title="Conditie niet voldaan"><Icon name="warning" size={14} sx={{ color: T.amber }} /></Tooltip>}
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    );
  };

  const renderStep3 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
        Taken worden proportioneel verdeeld over werkdagen (weekenden worden overgeslagen).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField label="Startdatum" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={inputSx} />
        <TextField label="Einddatum" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={inputSx} />
      </Box>
      {startDate && endDate && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: `${T.primary}08`, border: `1px solid ${T.primary}15` }}>
          <Icon name="info" size={18} sx={{ color: T.primary, marginTop: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>{selectedTaskCount} taken worden verdeeld over {startDate} t/m {endDate}</Typography>
        </Box>
      )}
    </Box>
  );

  const renderStep4 = () => {
    const roleToKeyword = { 'Projectleider': 'Projectleider', 'Accountmanager': 'Accountmanager', 'Regisseur': 'Regisseur', 'Camera Operator': 'Camera', 'Producer': 'Producer', 'Editor': 'Editor', 'Animator': 'Animator', 'Copywriter': 'Copywriter', 'Productie-assistent': 'Productie', 'Management': 'MT' };
    const allOptions = personen.map(p => ({ id: p.id, name: p.naam || p.name || 'Onbekend', functie: p.functie || '' }));
    const getOptionsForRole = (role) => { const kw = roleToKeyword[role]; if (!kw) return allOptions; const f = allOptions.filter(p => p.functie.toLowerCase().includes(kw.toLowerCase())); return f.length > 0 ? f : allOptions; };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>Wijs per rol een persoon toe.</Typography>
        {uniqueRoles.map(role => (
          <Box key={role} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: '#fff', bgcolor: roleColors[role] || T.outline, px: 1.25, py: 0.3, borderRadius: '9999px', minWidth: 100, textAlign: 'center' }}>{role}</Typography>
            <Autocomplete options={getOptionsForRole(role)} getOptionLabel={(o) => o.name || ''} value={roleAssignments[role] ? getOptionsForRole(role).find(p => p.id === roleAssignments[role]?.id) || null : null} onChange={(_, v) => handleRoleAssign(role, v)} renderInput={(params) => <TextField {...params} size="small" placeholder="Selecteer…" sx={inputSx} />} sx={{ flex: 1 }} isOptionEqualToValue={(o, v) => o.id === v?.id} />
          </Box>
        ))}
      </Box>
    );
  };

  const renderStep5 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!generatedResult && !pushResult && (
        <>
          <SectionLabel>Overzicht</SectionLabel>
          <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.75rem', p: 2 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text }}><strong>Template:</strong> {selectedTemplate?.name}</Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text }}><strong>Periode:</strong> {startDate} t/m {endDate}</Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text }}><strong>Taken:</strong> {selectedTaskCount} van {totalTasks} — <strong>{totalHours}u</strong></Typography>
          </Box>

          {templateTree?.phases?.map(phase => {
            const pt = phase.tasks?.filter(t => taskSelections[t.id]?.included) || [];
            if (pt.length === 0) return null;
            return (
              <Box key={phase.id}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text, mt: 1 }}>{phase.name}</Typography>
                {pt.map(task => {
                  const sel = taskSelections[task.id];
                  return (
                    <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: 2 }}>
                      <Icon name="check_circle" size={14} sx={{ color: T.emerald }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text, flex: 1 }}>{sel?.name || task.name}</Typography>
                      {sel?.role && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: '#fff', bgcolor: roleColors[sel.role] || T.outline, px: 0.75, py: 0.15, borderRadius: '9999px' }}>{sel.role}</Typography>}
                      <TextField value={sel?.hours || 0} onChange={(e) => handleTaskFieldChange(task.id, 'hours', parseFloat(e.target.value) || 0)} size="small" type="number" sx={{ width: 80, ...inputSx }} InputProps={{ endAdornment: <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>u</Typography> }} />
                    </Box>
                  );
                })}
              </Box>
            );
          })}

          <Box onClick={!loading ? handleGenerate : undefined} sx={{
            mt: 2, py: 1.25, borderRadius: '0.75rem', cursor: !loading ? 'pointer' : 'default',
            background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
            color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
            boxShadow: '0 4px 12px rgba(99,89,131,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
            transition: 'all 0.2s', opacity: loading ? 0.7 : 1,
            '&:hover': !loading ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
          }}>
            <Icon name={loading ? 'hourglass_empty' : 'playlist_add'} size={18} sx={{ color: '#fff' }} />
            {loading ? 'Concept genereren…' : 'Genereer concept taken'}
          </Box>
        </>
      )}

      {generatedResult && !pushResult && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: T.emeraldBg, border: `1px solid ${T.emerald}20` }}>
            <Icon name="check_circle" size={18} sx={{ color: T.emerald }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.emerald, fontWeight: 600 }}>{generatedResult.included_tasks} van {generatedResult.total_tasks} taken als concept aangemaakt!</Typography>
          </Box>

          <SectionLabel>Concept taken</SectionLabel>
          {generatedResult.tasks?.filter(t => t.is_included).map(task => (
            <Box key={task.id} sx={{ bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon name="check_circle" size={14} sx={{ color: T.emerald }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text, flex: 1 }}>{task.name}</Typography>
                {task.role && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: '#fff', bgcolor: roleColors[task.role] || T.outline, px: 0.75, py: 0.15, borderRadius: '9999px' }}>{task.role}</Typography>}
                <TextField value={task.estimated_hours} onChange={(e) => { const val = parseFloat(e.target.value) || 0; setGeneratedResult(prev => ({ ...prev, tasks: prev.tasks.map(t => t.id === task.id ? { ...t, estimated_hours: val } : t) })); }} size="small" type="number" sx={{ width: 80, ...inputSx }} InputProps={{ endAdornment: <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>u</Typography> }} />
                <TextField value={task.start_date || ''} onChange={(e) => setGeneratedResult(prev => ({ ...prev, tasks: prev.tasks.map(t => t.id === task.id ? { ...t, start_date: e.target.value } : t) }))} size="small" type="date" sx={{ width: 130, ...inputSx }} InputLabelProps={{ shrink: true }} />
                <TextField value={task.end_date || ''} onChange={(e) => setGeneratedResult(prev => ({ ...prev, tasks: prev.tasks.map(t => t.id === task.id ? { ...t, end_date: e.target.value } : t) }))} size="small" type="date" sx={{ width: 130, ...inputSx }} InputLabelProps={{ shrink: true }} />
              </Box>
              {task.assignee_name && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, pl: 3 }}>Toegewezen: {task.assignee_name}</Typography>}
            </Box>
          ))}

          <Box onClick={!pushing ? handlePushToAirtable : undefined} sx={{
            mt: 2, py: 1.25, borderRadius: '0.75rem', cursor: !pushing ? 'pointer' : 'default',
            background: `linear-gradient(135deg, ${T.emerald} 0%, #059669 100%)`,
            color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
            boxShadow: '0 4px 12px rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
            transition: 'all 0.2s', opacity: pushing ? 0.7 : 1,
            '&:hover': !pushing ? { boxShadow: '0 6px 20px rgba(16,185,129,0.35)' } : {},
          }}>
            <Icon name={pushing ? 'hourglass_empty' : 'send'} size={18} sx={{ color: '#fff' }} />
            {pushing ? 'Pushen…' : `Push ${generatedResult.included_tasks} taken naar Airtable`}
          </Box>
        </>
      )}

      {pushResult && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: T.emeraldBg, border: `1px solid ${T.emerald}20` }}>
          <Icon name="check_circle" size={18} sx={{ color: T.emerald }} />
          <Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.emerald }}>{pushResult.created} taken aangemaakt in Airtable!</Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.secondary }}>Je kunt dit venster sluiten.</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderCurrentStep = () => {
    switch (activeStep) { case 0: return renderStep1(); case 1: return renderStep2Conditions(); case 2: return renderStep3Kanban(); case 3: return renderStep3(); case 4: return renderStep4(); case 5: return renderStep5(); default: return null; }
  };

  /* ── Main render ─────────────────────────────────────────────────────── */
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: '0.75rem', height: '92vh', maxHeight: '92vh' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${T.surfaceLow}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="playlist_add" size={22} sx={{ color: T.tertiary }} />
          <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>Projecttaken genereren</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {onOpenTemplateManager && (
            <Tooltip title="Templates beheren">
              <Box onClick={onOpenTemplateManager} sx={{ width: 32, height: 32, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
                <Icon name="settings" size={18} sx={{ color: T.outline }} />
              </Box>
            </Tooltip>
          )}
          <Box onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
            <Icon name="close" size={18} sx={{ color: T.outline }} />
          </Box>
        </Box>
      </Box>

      {/* Step indicator */}
      <Box sx={{ px: 3, py: 1.25, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: `1px solid ${T.surfaceLow}` }}>
        {STEPS.map((label, idx) => (
          <Box key={label} onClick={() => idx < activeStep && setActiveStep(idx)} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '9999px',
            bgcolor: activeStep === idx ? T.primaryContainer : idx < activeStep ? T.emeraldBg : T.surfaceLow,
            cursor: idx < activeStep ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}>
            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, lineHeight: 1, color: activeStep === idx ? T.primary : idx < activeStep ? T.emerald : T.outlineVariant }}>
              {idx < activeStep ? '✓' : idx + 1}
            </Typography>
            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: activeStep === idx ? 700 : 500, lineHeight: 1, color: activeStep === idx ? T.primary : idx < activeStep ? T.emerald : T.outlineVariant }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Content */}
      <DialogContent sx={{ pt: 3, minHeight: 400, flex: 1 }}>
        {error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}20` }}>
            <Icon name="error" size={18} sx={{ color: T.error }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.error, flex: 1 }}>{error}</Typography>
            <Box onClick={() => setError(null)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}><Icon name="close" size={14} sx={{ color: T.error }} /></Box>
          </Box>
        )}
        {loading && activeStep < 5 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Box sx={{ width: 28, height: 28, border: `3px solid ${T.outlineVariant}`, borderTop: `3px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          </Box>
        ) : renderCurrentStep()}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 3, py: 2, borderTop: `1px solid ${T.surfaceLow}` }}>
        <Box onClick={activeStep > 0 ? () => setActiveStep(prev => prev - 1) : undefined} sx={{
          px: 2, py: 0.75, borderRadius: '0.5rem', cursor: activeStep > 0 ? 'pointer' : 'default',
          fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: activeStep > 0 ? T.secondary : T.outlineVariant,
          '&:hover': activeStep > 0 ? { bgcolor: T.surfaceLow } : {},
        }}>
          Vorige
        </Box>
        {activeStep < STEPS.length - 1 ? (
          <Box onClick={canProceed() ? () => setActiveStep(prev => prev + 1) : undefined} sx={{
            px: 2.5, py: 0.75, borderRadius: '0.5rem',
            cursor: canProceed() ? 'pointer' : 'default',
            background: canProceed() ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)` : T.surfaceHigh,
            color: canProceed() ? '#fff' : T.outlineVariant,
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
            boxShadow: canProceed() ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
            transition: 'all 0.2s',
            '&:hover': canProceed() ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
          }}>
            Volgende
          </Box>
        ) : pushResult ? (
          <Box onClick={onClose} sx={{
            px: 2.5, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
            background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
            color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
            boxShadow: '0 4px 12px rgba(99,89,131,0.25)', transition: 'all 0.2s',
            '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' },
          }}>
            Sluiten
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
};

export default PreproductionWizard;
